import React, { useState } from "react";
import "components/Screens/CustomerOnboarding/AddPaymentMethod/AddPaymentMethod.scss";
import { useDispatch } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

import Button from "components/common/Button";
import AddPaymentLoaderModal from "components/Screens/CustomerOnboarding/AddPaymentLoaderModal";
import { AlertType } from "components/common/Snackbar";

import { showSnackbar } from "store/actions/Utility";
import { customerAddStripePayment } from "services/customer";

import { LockOutlined as LockOutlinedIcon } from "@mui/icons-material";

import { CUSTOMER_PAYMENT } from "constants/routes";

import "./AddPayment.scss";

function AddCreditCard({ loading, setLoading, navigate, customerId, update, change }) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { stripeFc = false } = useFlags();
  const [isFormComplete, setIsFormComplete] = useState(false);

  const getButtonLabel = () => {
    if (update) return "Update payment method";
    if (change) return "Change payment method";
    return "Add payment method";
  };

  const buttonLabel = getButtonLabel();

  const handleCreditCardSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + CUSTOMER_PAYMENT,
        },
        redirect: "if_required",
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: error.message,
          })
        );

        setLoading(false);
      } else if (setupIntent && setupIntent.status === "succeeded") {
        await customerAddStripePayment({ setupIntentId: setupIntent?.id }, customerId);
        setLoading(false);
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.success,
            message: "Payment method added successfully.",
          })
        );

        setTimeout(() => {
          navigate(CUSTOMER_PAYMENT);
        }, 2000);
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: error.message || "Failed to save payment method",
        })
      );

      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleCreditCardSubmit} className="credit-card-form">
        <div className="payment-element-container">
          <PaymentElement
            options={{
              layout: {
                type: "auto",
              },
              fields: {
                billingDetails: {
                  name: "auto",
                  email: "auto",
                  phone: "auto",
                  address: {
                    country: "auto",
                    line1: "auto",
                    line2: "auto",
                    city: "auto",
                    state: "auto",
                    postalCode: "auto",
                  },
                },
              },
            }}
            onChange={(event) => {
              setIsFormComplete(event.complete);
            }}
          />
        </div>

        {stripeFc && (
          <p className="para-body-s-regular color-text-body-primary" style={{ marginTop: "16px" }}>
            Your payment information is encrypted and securely shared with Stripe for processing. We never store your
            card or bank account information.
          </p>
        )}

        {PaymentElement && (
          <div className="w-full" style={{ marginTop: "16px" }}>
            <Button
              isFullWidth
              onClick={handleCreditCardSubmit}
              name={buttonLabel}
              startIcon={<LockOutlinedIcon />}
              size="large"
              label={buttonLabel}
              isDisabled={!stripe || !elements || !isFormComplete}
              isLoading={loading}
            />
          </div>
        )}
      </form>

      {loading && (
        <AddPaymentLoaderModal
          title={(() => {
            if (update) return "Hang tight, we’re updating your payment method…";
            if (change) return "Hang tight, we’re changing your payment method…";
            return "Hang tight, we’re adding your payment method…";
          })()}
          description="Just a moment while we securely save your payment details. This won’t take long."
          open={loading}
        />
      )}
    </div>
  );
}

export default AddCreditCard;
